/* ==========================================================================
   Hero Cards - Asian American Forward Website
   ========================================================================== */

import { preventModalScroll, allowModalScroll } from './utils/scroll-prevention.js';

import { setup3DCardHover, isGSAPLoaded } from './utils/gsap-animations.js';
import { gsap } from 'gsap';

let cardElements = [];
let currentTopIndex = 0;
let shuffleInterval;
let isShuffling = false;
let isAnyCardHovered = false;
let initialShuffleTimeout = null; // Track the initial shuffle timeout

export function initializeHeroCards() {
  console.log('🃏 Initializing hero cards...');
  
  // Get all card elements
  cardElements = Array.from(document.querySelectorAll('.hero-card'));
  
  if (cardElements.length === 0) {
    console.warn('No hero cards found');
    return;
  }
  
  // Set up initial card positions
  updateCardPositions();
  
  // Force immediate positioning update to avoid flashing
  setTimeout(() => updateCardPositions(), 10);
  
  // Add click handlers for card expansion and mouse effects
  cardElements.forEach((card, index) => {
    card.addEventListener('click', () => expandCard(card));
    card.setAttribute('tabindex', '0');
    card.setAttribute('role', 'button');
    card.setAttribute('aria-label', `View image ${index + 1} in fullscreen`);
    
    // Use GSAP 3D effects if available, otherwise fallback to vanilla JS
    if (isGSAPLoaded()) {
      setup3DCardHover(card);
    } else {
      setup3DMouseEffect(card);
    }
  });
  
  // Listen for pause/resume events from GSAP hover effects
  window.addEventListener('pauseCardCycling', pauseShuffle);
  window.addEventListener('resumeCardCycling', resumeShuffle);
  
  // Start auto-shuffle animation
  startAutoShuffle();
  
  // Fallback: Ensure shuffling starts even if something goes wrong
  setTimeout(() => {
    if (!shuffleInterval) {
      console.log('🔄 Fallback: Starting auto-shuffle as it appears to have failed to start');
      startAutoShuffle();
    }
  }, 5000); // Check after 5 seconds
  
  // Pause shuffle on hover (backup for container hover)
  const heroCardsContainer = document.querySelector('.hero-cards-container');
  if (heroCardsContainer) {
    heroCardsContainer.addEventListener('mouseenter', pauseShuffle);
    heroCardsContainer.addEventListener('mouseleave', resumeShuffle);
  }
  
  console.log('✅ Hero cards initialized with auto-shuffle');
}

/* ==========================================================================
   Card Positioning & Animation
   ========================================================================== */

function updateCardPositions() {
  cardElements.forEach((card, index) => {
    const stackPosition = (index - currentTopIndex + cardElements.length) % cardElements.length;
    card.setAttribute('data-card-index', stackPosition.toString());
    
    // Reset any custom CSS properties that might have been set during hover
    card.style.removeProperty('--card-tilt-x');
    card.style.removeProperty('--card-tilt-y');
    card.style.removeProperty('--card-translate-z');
    
    // Use GSAP for positioning if available
    if (isGSAPLoaded()) {
      const isInitial = !card.hasAttribute('data-initial-position');
      
      // Clear any CSS transforms that might interfere
      card.style.transform = '';
      
      // Define positions
      const positions = {
        0: { x: 0, y: 0, scale: 1, zIndex: 40 },
        1: { x: 25, y: 25, scale: 0.95, zIndex: 30 },
        2: { x: 50, y: 50, scale: 0.9, zIndex: 20 },
        3: { x: 75, y: 75, scale: 0.85, zIndex: 10 }
      };
      const targetPos = positions[stackPosition] || positions[3];
      
      if (isInitial) {
        // Set initial position immediately without animation
        gsap.set(card, {
          x: targetPos.x,
          y: targetPos.y,
          scale: targetPos.scale,
          zIndex: targetPos.zIndex,
          rotationX: 0,
          rotationY: 0,
          z: 0,
          clearProps: "transform" // Clear any CSS transforms
        });
        console.log(`🃏 Initial GSAP positioning for card ${index}:`, targetPos);
      }
    }
  });
  
  // Mark that initial positioning is complete
  cardElements.forEach(card => {
    card.setAttribute('data-initial-position', 'true');
  });
}

function animateCardToPosition(card, stackPosition) {
  // Define positions for each stack position
  const positions = {
    0: { x: 0, y: 0, scale: 1, zIndex: 40 },
    1: { x: 25, y: 25, scale: 0.95, zIndex: 30 },
    2: { x: 50, y: 50, scale: 0.9, zIndex: 20 },
    3: { x: 75, y: 75, scale: 0.85, zIndex: 10 }
  };
  
  const targetPos = positions[stackPosition] || positions[3];
  
  // Use GSAP to animate to the new position
  gsap.to(card, {
    x: targetPos.x,
    y: targetPos.y,
    scale: targetPos.scale,
    zIndex: targetPos.zIndex,
    duration: 0.6,
    ease: "power2.out"
  });
}

function shuffleCards() {
  if (isShuffling || cardElements.length === 0 || isAnyCardHovered) return;
  
  isShuffling = true;
  console.log('🔄 Starting card shuffle...');
  
  // Move to next card
  currentTopIndex = (currentTopIndex + 1) % cardElements.length;
  
  // Animate all cards to their new positions
  cardElements.forEach((card, index) => {
    const stackPosition = (index - currentTopIndex + cardElements.length) % cardElements.length;
    card.setAttribute('data-card-index', stackPosition.toString());
    
    // Reset any hover effects and CSS properties
    card.style.removeProperty('--card-tilt-x');
    card.style.removeProperty('--card-tilt-y');
    card.style.removeProperty('--card-translate-z');
    card.style.transform = ''; // Clear any CSS transforms
    
    // Clear any GSAP tilt effects that might be lingering
    if (isGSAPLoaded()) {
      gsap.set(card, {
        rotationX: 0,
        rotationY: 0,
        z: 0
      });
      
      // Then animate to new position
      animateCardToPosition(card, stackPosition);
    }
  });
  
  // Allow next shuffle after animation completes
  setTimeout(() => {
    isShuffling = false;
    console.log('✅ Card shuffle complete');
  }, 600); // Match GSAP animation duration
}

/* ==========================================================================
   Auto-Shuffle Functionality
   ========================================================================== */

function startAutoShuffle() {
  // Clear any existing interval first
  if (shuffleInterval) {
    clearInterval(shuffleInterval);
    shuffleInterval = null;
  }
  
  // Clear any existing initial shuffle timeout
  if (initialShuffleTimeout) {
    clearTimeout(initialShuffleTimeout);
    initialShuffleTimeout = null;
  }
  
  // Shuffle every 4 seconds (back to original timing as requested)
  shuffleInterval = setInterval(() => {
    if (!isAnyCardHovered && !isShuffling) {
      shuffleCards();
    }
  }, 4000);
  
  // Only set initial shuffle timeout if this is the first start (not a resume)
  if (!document.querySelector('.hero-card[data-initial-position="true"]')) {
    // Start with an immediate shuffle after a short delay to make it more noticeable
    initialShuffleTimeout = setTimeout(() => {
      if (!isAnyCardHovered && !isShuffling) {
        shuffleCards();
      }
      initialShuffleTimeout = null;
    }, 2000); // First shuffle after 2 seconds
  }
}

function resumeAutoShuffle() {
  // Clear any existing interval first
  if (shuffleInterval) {
    clearInterval(shuffleInterval);
    shuffleInterval = null;
  }
  
  // Just start the regular interval without initial delay when resuming
  shuffleInterval = setInterval(() => {
    if (!isAnyCardHovered && !isShuffling) {
      shuffleCards();
    }
  }, 4000);
}

function pauseShuffle() {
  console.log('⏸️ Pausing card shuffle');
  if (shuffleInterval) {
    clearInterval(shuffleInterval);
    shuffleInterval = null;
  }
  
  // Also clear any pending initial shuffle timeout
  if (initialShuffleTimeout) {
    clearTimeout(initialShuffleTimeout);
    initialShuffleTimeout = null;
  }
  
  isAnyCardHovered = true; // Set the flag to prevent new shuffles
}

function resumeShuffle() {
  console.log('▶️ Resuming card shuffle');
  isAnyCardHovered = false;
  
  // Only restart if there's no existing interval
  if (!shuffleInterval && !isShuffling) {
    resumeAutoShuffle(); // Use resume function instead of start to avoid initial delay
  }
}

/* ==========================================================================
   3D Mouse Effect (Weight-based Tilting)
   ========================================================================== */

function setup3DMouseEffect(card) {
  let isHovering = false;
  let hoverTimeout = null; // Track hover timeout
  
  card.addEventListener('mouseenter', () => {
    const stackIndex = parseInt(card.getAttribute('data-card-index')) || 0;
    // Only allow hover on top card (index 0)
    if (stackIndex !== 0) return;
    
    console.log('🎯 Vanilla JS card hover started');
    
    // Clear any pending resume timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }
    
    isHovering = true;
    isAnyCardHovered = true; // Prevent shuffling while top card is hovered
    pauseShuffle(); // Explicitly pause shuffling
    card.style.transition = 'all 0.1s ease-out'; // Smooth transition for hover
  });
  
  card.addEventListener('mouseleave', () => {
    const stackIndex = parseInt(card.getAttribute('data-card-index')) || 0;
    if (stackIndex !== 0) return;
    
    console.log('🎯 Vanilla JS card hover ended');
    
    isHovering = false;
    card.style.transition = 'all 0.4s ease-out'; // Smooth transition back
    
    // Reset transforms - CSS will handle base positioning
    resetCardTransform(card);
    
    // Resume shuffling after a delay (same as GSAP version)
    hoverTimeout = setTimeout(() => {
      isAnyCardHovered = false;
      resumeShuffle();
      hoverTimeout = null;
    }, 2000); // Changed from 1000ms to 2000ms (2 seconds) as requested
  });
  
  card.addEventListener('mousemove', (e) => {
    const stackIndex = parseInt(card.getAttribute('data-card-index')) || 0;
    // Only allow 3D effect on top card
    if (!isHovering || stackIndex !== 0) return;
    
    const rect = card.getBoundingClientRect();
    const cardCenterX = rect.left + rect.width / 2;
    const cardCenterY = rect.top + rect.height / 2;
    
    // Calculate mouse position relative to card center (-1 to 1)
    const mouseX = (e.clientX - cardCenterX) / (rect.width / 2);
    const mouseY = (e.clientY - cardCenterY) / (rect.height / 2);
    
    // Calculate tilt intensity (mouse acts as weight)
    const maxTilt = 15; // degrees
    const tiltX = mouseY * maxTilt; // Vertical mouse movement tilts around X axis
    const tiltY = -mouseX * maxTilt; // Horizontal mouse movement tilts around Y axis
    
    // Apply 3D tilt using CSS custom properties
    card.style.setProperty('--card-tilt-x', `${tiltX}deg`);
    card.style.setProperty('--card-tilt-y', `${tiltY}deg`);
    card.style.setProperty('--card-translate-z', '30px'); // Lift the card
    card.style.setProperty('--card-z-index', '50'); // Ensure it stays on top
  });
}

function resetCardTransform(card) {
  // Simply remove the hover transforms - CSS will handle the base positioning
  card.style.removeProperty('--card-tilt-x');
  card.style.removeProperty('--card-tilt-y');
  card.style.removeProperty('--card-translate-z');
  card.style.removeProperty('--card-z-index');
}

/* ==========================================================================
   Card Expansion/Modal Functionality
   ========================================================================== */

function expandCard(cardElement) {
  // Only allow expansion for hero cards, not event cards
  if (!cardElement.classList.contains('hero-card')) {
    console.log('🚫 Ignoring non-hero card expansion attempt');
    return;
  }
  
  pauseShuffle(); // Stop shuffling during modal
  
  const image = cardElement.querySelector('.hero-card-image');
  if (!image) return;
  
  const modal = document.getElementById('card-modal');
  const modalImage = document.getElementById('modal-image');
  
  if (!modal || !modalImage) {
    console.warn('Card modal elements not found');
    return;
  }
  
  // Set modal image
  modalImage.src = image.src;
  modalImage.alt = image.alt;
  
  // Prevent scrolling
  preventModalScroll();
  
  // Show modal
  modal.style.display = 'flex';
  modal.setAttribute('aria-hidden', 'false');
  modalImage.focus({ preventScroll: true });
  
  const closeModal = () => {
    modal.style.display = 'none';
    modal.setAttribute('aria-hidden', 'true');
    
    // Re-enable scrolling
    allowModalScroll();
    
    cardElement.focus({ preventScroll: true }); // Return focus to card
    resumeShuffle(); // Resume shuffling
    
    // Remove event listeners
    modal.removeEventListener('click', closeModal);
    document.removeEventListener('keydown', handleKeydown);
  };
  
  // Close modal when clicking anywhere on the modal (including the image)
  modal.addEventListener('click', closeModal);
  
  // Close on Escape key
  const handleKeydown = (e) => {
    if (e.key === 'Escape') {
      closeModal();
      document.removeEventListener('keydown', handleKeydown);
    }
  };
  
  document.addEventListener('keydown', handleKeydown);
}

/* ==========================================================================
   Responsive Behavior
   ========================================================================== */

function handleResize() {
  // Pause shuffle during resize
  pauseShuffle();
  
  // Resume after a brief delay
  setTimeout(() => {
    if (!document.querySelector('.card-modal[aria-hidden="false"]')) {
      resumeShuffle();
    }
  }, 500);
}

// Add resize listener
window.addEventListener('resize', handleResize);

/* ==========================================================================
   Reduced Motion Support
   ========================================================================== */

function checkReducedMotion() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    console.log('🔄 Reduced motion preference detected, but keeping card shuffle active');
    // Note: We're not calling pauseShuffle() here anymore to allow cards to work with Windows animations off
  }
  
  return prefersReducedMotion;
}

// Check reduced motion on init
if (typeof window !== 'undefined') {
  checkReducedMotion();
  
  // Listen for changes in motion preference
  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', checkReducedMotion);
}

/* ==========================================================================
   Public API
   ========================================================================== */

export function pauseCardShuffle() {
  pauseShuffle();
}

export function resumeCardShuffle() {
  // Always resume card shuffling regardless of motion preferences
  resumeShuffle();
}

export function triggerCardShuffle() {
  // Always allow card shuffling regardless of motion preferences
  shuffleCards();
} 